import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';

/**
 * Empties the query cache when a signed-in session ENDS or is REPLACED (another
 * account, or the same one with another role).
 *
 * Several reads answer differently by role under ONE key — the overview is the
 * platform's for an admin and the reader's own for anyone else, and so are the
 * documents, decks and agents. Without this, signing out of an admin session and
 * into a guest one in the same tab would serve the guest the admin's cached
 * answers until they went stale.
 *
 * The tab's FIRST sign-in is left alone on purpose: the cache is empty then, and
 * clearing it would drop the queries the landing page has just started.
 */
export function SessionScopedCache({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const identity = user ? `${user.id}:${user.role}` : null;
  const previous = useRef(identity);

  useEffect(() => {
    if (previous.current === identity) return;
    if (previous.current !== null) queryClient.clear();
    previous.current = identity;
  }, [identity, queryClient]);

  return <>{children}</>;
}
